import React, { useEffect, useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import {
  getEditItem,
  getIsItemModalOpen,
  toggleItemModal,
} from "../../features/items/itemsSlice";
import { Camera, CameraResultType, CameraSource } from "@capacitor/camera";
import { GiCancel } from "react-icons/gi";

import styles from "./ItemFormModal.module.scss";
import { IFormInput } from "../../types/item";

type Props = {
  handleSaveItem: (data: IFormInput) => void;
};

const ItemFormModal = ({ handleSaveItem }: Props) => {
  const dispatch = useAppDispatch();
  const editItem = useAppSelector(getEditItem);
  const isItemModalOpen = useAppSelector(getIsItemModalOpen);

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [capturedPhoto, setCapturedPhoto] = useState<string | null>(null);
  const [cameraError, setCameraError] = useState<string | null>(null);
  const [imageError, setImageError] = useState<string | null>(null);

  const {
    handleSubmit,
    register,
    reset,
    watch,
    formState: { errors },
  } = useForm<IFormInput>({ defaultValues: { name: "", description: "", weight: "", isinset: false } });

  const watchImage = watch("image");

  const handleCapturePhoto = async () => {
    setCameraError(null);
    setImageError(null);
    try {
      const image = await Camera.getPhoto({
        quality: 50,
        allowEditing: false,
        resultType: CameraResultType.Uri,
        source: CameraSource.Camera,
      });
      setCapturedPhoto(image?.webPath || null);
      setImagePreview(image?.webPath || null);
    } catch (error) {
      setCameraError("કેમેરા માટે ની Permission નથી કૃપા કરી Permission આપો.");
    }
  };

  const handleRemovePhoto = () => {
    setCapturedPhoto(null);
    setImagePreview(editItem?.image || null);
  };

  const handleOnSubmit: SubmitHandler<IFormInput> = async (data: any) => {
    // Validate image is present
    const hasNewImage = capturedPhoto || (data.image && data.image.length > 0);
    const hasExistingImage = editItem?.image && !capturedPhoto && (!data.image || data.image.length === 0);

    if (!hasNewImage && !hasExistingImage) {
      setImageError("આઈટમ ની ફોટો જરૂરી છે");
      return;
    }

    setImageError(null);
    setIsLoading(true);
    await handleSaveItem({ ...data, capturedPhoto });
    handleCloseFormModal();
    setIsLoading(false);
  };

  const handleCloseFormModal = () => {
    reset();
    setImagePreview(null);
    setCapturedPhoto(null);
    setCameraError(null);
    setImageError(null);
    dispatch(toggleItemModal(false));
  };

  useEffect(() => {
    if (editItem && editItem.id) {
      reset({
        name: editItem.name,
        description: editItem.description,
        weight: editItem.weight || "",
        isinset: Boolean(editItem.isinset)
      });
      setImagePreview(editItem.image || null);
      setCapturedPhoto(null);
      setImageError(null);
    } else {
      reset({ name: "", description: "", weight: "", isinset: false });
      setImagePreview(null);
      setCapturedPhoto(null);
      setImageError(null);
    }
  }, [editItem, isItemModalOpen]);

  useEffect(() => {
    if (watchImage && watchImage.length > 0) {
      const file = watchImage[0];
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result as string);
        setCapturedPhoto(null);
        setImageError(null);
      };
      reader.readAsDataURL(file);
    }
  }, [watchImage]);

  return (
    <>
      {isItemModalOpen === true && (
        <div className={styles.ItemFormModal}>
          <div className="relative h-[100vh] w-[100vw] overflow-y-scroll lg:overflow-auto top-0 lg:top-10 lg:mx-auto p-5 border lg:w-96 lg:h-fit shadow-lg lg:rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-2xl text-center font-bold leading-6 text-primary-1">
                આઈટમ ની માહિતી
              </h3>
              <div className="mt-2 py-3">
                <form onSubmit={handleSubmit(handleOnSubmit)}>
                  <div className="grid gap-4">
                    <div className="mt-6">
                      <div className="bg-gradient-to-r from-primary-1 to-primary-2 h-16 lg:h-14 w-full relative rounded">
                        <input
                          className="text-primary-1 focus:outline-none px-3 py-1 font-semibold text-xl placeholder:text-primary-2 absolute top-[2px] left-[2px] w-[calc(100%-4px)] h-[calc(100%-4px)] rounded"
                          type="text"
                          placeholder="આઈટમનુ નામ દાખલ કરો"
                          {...register("name", { required: true })}
                        />
                      </div>
                      {errors.name && errors.name.type === "required" && (
                        <span className="text-red-500">
                          આઈટમનુ નામ દાખલ કરો
                        </span>
                      )}
                    </div>

                    <div className="mt-3">
                      <div className="bg-gradient-to-r from-primary-1 to-primary-2 h-28 w-full relative rounded">
                        <textarea
                          rows={5}
                          className="text-primary-1 focus:outline-none px-3 py-1 font-semibold text-xl placeholder:text-primary-2 absolute top-[2px] left-[2px] w-[calc(100%-4px)] h-[calc(100%-4px)] rounded"
                          placeholder="આઈટમનુ ડિસ્ક્રિપ્શન દાખલ કરો"
                          {...register("description", { required: false })}
                        />
                      </div>
                      {errors.description &&
                        errors.description.type === "required" && (
                          <span className="text-red-500">
                            આઈટમનુ ડિસ્ક્રિપ્શન દાખલ કરો
                          </span>
                        )}
                    </div>

                    <div className="mt-3">
                      <div className="bg-gradient-to-r from-primary-1 to-primary-2 h-16 lg:h-14 w-full relative rounded">
                        <input
                          className="text-primary-1 focus:outline-none px-3 py-1 font-semibold text-xl placeholder:text-primary-2 absolute top-[2px] left-[2px] w-[calc(100%-4px)] h-[calc(100%-4px)] rounded"
                          type="number"
                          step="0.0001"
                          min="0"
                          placeholder="આઈટમનુ વજન કિલો માં દાખલ કરો"
                          {...register("weight", { required: false, valueAsNumber: true })}
                        />
                      </div>
                    </div>

                    <div className="mt-3 flex items-center justify-center">
                      <label className="flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          className="w-6 h-6 text-primary-1 border-2 border-primary-1 rounded focus:ring-primary-1"
                          {...register("isinset")}
                        />
                        <span className="ml-3 text-primary-1 font-semibold text-xl">
                          આઈટમ સેટ મા આવસે?
                        </span>
                      </label>
                    </div>

                    <div className="mt-3">
                      {/* <label className="text-primary-1 font-semibold text-lg mb-2 block">
                        આઈટમ ની ફોટો
                      </label> */}

                      {/* Camera Button */}
                      <div className="flex justify-center mb-4">
                        <button
                          type="button"
                          onClick={handleCapturePhoto}
                          className="shadow rounded-full text-lg text-white bg-gradient-to-r from-primary-1 to-primary-2 font-bold px-6 py-3"
                        >
                          ફોટો પાડો
                        </button>
                      </div>

                      {cameraError && (
                        <p className="text-red-500 text-center text-sm mb-2">{cameraError}</p>
                      )}

                      {imageError && (
                        <p className="text-red-500 text-center text-sm mb-2">{imageError}</p>
                      )}

                      {/* Image Preview */}
                      {imagePreview && (
                        <div className="relative flex justify-center mb-4">
                          <div className="relative border-4 border-primary-1 rounded-lg p-2 inline-block">
                            <GiCancel
                              onClick={handleRemovePhoto}
                              className="absolute z-10 shadow bg-white -top-3 -right-3 text-3xl text-primary-1 border-2 border-primary-1 p-1 rounded-full cursor-pointer"
                            />
                            <img
                              src={imagePreview}
                              alt="Preview"
                              className="w-32 h-32 object-cover rounded"
                            />
                          </div>
                        </div>
                      )}

                      {/* {!imagePreview && (
                        <div className="flex justify-center mb-4">
                          <div className="w-32 h-32 bg-gray-200 rounded-lg border-2 border-dashed border-primary-1 flex items-center justify-center">
                            <span className="text-gray-500 text-sm text-center">
                              ફોટો નથી
                            </span>
                          </div>
                        </div>
                      )} */}
                    </div>

                    <div className="mt-3 text-center">
                      <button
                        className="mx-auto shadow disabled:opacity-50 rounded-3xl text-3xl lg:text-xl text-white bg-gradient-to-r from-primary-1 to-primary-2 font-bold px-10 py-2"
                        type="submit"
                        disabled={isLoading}
                      >
                        {isLoading ? "રાહ જુઓ..." : "આગળ વધો"}
                      </button>
                    </div>

                    <div className="mt-1 text-center">
                      <button
                        className="mx-auto shadow disabled:opacity-50 rounded-3xl text-3xl lg:text-xl text-white bg-gradient-to-r from-primary-1 to-primary-2 font-bold px-10 py-2"
                        type="button"
                        onClick={handleCloseFormModal}
                        disabled={isLoading}
                      >
                        બંધ કરો
                      </button>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ItemFormModal;
